import type { PhuongTienVanChuyen, PhuongTienVanChuyenInput } from '@/types/schemas';
import { ConfirmationDialog, ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog } from './components';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function PhuongTienVanChuyen() {
  const {
    data: shippingMethods,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    searchData
  } = useCRUD<PhuongTienVanChuyen, PhuongTienVanChuyenInput>({
    endpoint: QUERY_KEYS.PHUONG_TIEN_VAN_CHUYEN,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<PhuongTienVanChuyen>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : shippingMethods || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddShippingMethod = async (data: PhuongTienVanChuyenInput) => {
    await addItem(data);
    await refreshData();
  };

  const handleUpdateShippingMethod = async (data: PhuongTienVanChuyenInput) => {
    if (selectedObj) {
      await updateItem(selectedObj.uuid, data);
      await refreshData();
    }
  };

  const handleSubmit = async (data: PhuongTienVanChuyenInput) => {
    try {
      if (formMode === 'add') {
        await handleAddShippingMethod(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateShippingMethod(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (err: unknown) {
      console.error('Error submitting form:', err);
    }
  };
  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
        <ActionBar
          onAddIconClick={handleAddClick}
          onEditIconClick={handleEditClick}
          onDeleteIconClick={handleDeleteClick}
          onCopyIconClick={handleCopyClick}
          onWatchIconClick={handleViewClick}
          onRefreshClick={async () => {
            await refreshData();
            if (selectedObj?.uuid) {
              await fetchDetail(selectedObj.uuid);
            }
          }}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <FormDialog
          mode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          onDeleteButtonClick={handleDeleteClick}
          onSubmit={handleSubmit}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <ConfirmationDialog
          onClose={handleCloseDelete}
          onConfirm={async () => {
            if (selectedObj) {
              await deleteItem(selectedObj.uuid);
              await refreshData();
              handleCloseDelete();
              clearSelection();
            }
          }}
        />
      )}
    </div>
  );
}
